/**
 * Wallet utilities for marketplace operations
 * Handles loading and decrypting wallet with passkey authentication
 */

import { fetchWithAuth } from '@/lib/api/client'
import { authenticatePasskey } from '@/lib/crypto/webauthn'
import { decryptWallet, type WalletInfo, type EncryptedWalletData } from '@/lib/crypto/wallet'

const API_URL = import.meta.env.VITE_API_URL!

// In-memory cache for loaded wallet (cleared on page refresh)
let cachedWallet: WalletInfo | null = null

/**
 * Load and decrypt the user's wallet
 * Uses passkey authentication and caches the result in memory
 * @returns The decrypted wallet info
 */
export async function loadWallet(): Promise<WalletInfo> {
  // Return cached wallet if available
  if (cachedWallet) {
    return cachedWallet
  }

  // Fetch encrypted wallet from backend
  const response = await fetchWithAuth(`${API_URL}/api/wallet`, {
    method: 'GET',
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('No wallet found. Please create a wallet first using /generate.')
    }
    throw new Error('Failed to fetch wallet')
  }

  const data = await response.json()
  const encryptedWallet: EncryptedWalletData = data.wallet

  // Authenticate with passkey to get PRF key
  const { prfKey } = await authenticatePasskey(encryptedWallet.credentialId)

  if (!prfKey) {
    throw new Error('Failed to obtain PRF key from passkey')
  }

  // Decrypt wallet with PRF key
  const wallet = await decryptWallet(encryptedWallet, prfKey)

  // Cache in memory
  cachedWallet = wallet

  return wallet
}

/**
 * Clear the cached wallet from memory
 * Called when user explicitly logs out or switches accounts
 */
export function clearWalletCache(): void {
  cachedWallet = null
}

/**
 * Check if wallet is already loaded in memory
 */
export function isWalletLoaded(): boolean {
  return cachedWallet !== null
}

/**
 * Get the cached wallet without triggering passkey auth
 * Returns null if wallet is not loaded
 */
export function getCachedWallet(): WalletInfo | null {
  return cachedWallet
}

/**
 * Manually set the cached wallet
 * Used when wallet is generated or restored through the useWallet hook
 */
export function setCachedWallet(wallet: WalletInfo): void {
  cachedWallet = wallet
}
